import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  styled,
  Divider,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useFormik } from "formik";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import {
  subDays,
  subMonths,
  format as formatDate,
  parseISO,
  isValid,
} from "date-fns";
import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { showToast } from "@/redux/actions/toast-actions";
import { useReportOptions } from "@/hooks/use-report-options";
import { createReport, updateReport } from "@/services/reports.service";
import { reportFormSchema } from "@/schemas/report.schema";
import {
  getReportTypeMeta,
  getReportStatusMeta,
  formatOptionLabel,
  periods,
} from "../../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 480,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
}));

const PeriodButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  flex: 1,
  borderRadius: 8,
  textTransform: "none",
  fontSize: "0.8rem",
  fontFamily: "inherit",
  border: "none",
  cursor: "pointer",
  fontWeight: isActive ? 600 : 400,
  padding: theme.spacing(1, 1.5),
  backgroundColor: isActive
    ? theme.palette.primary.main
    : theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.06)"
      : theme.palette.grey[100],
  color: isActive ? "#fff" : theme.palette.text.secondary,
}));

const dateFmt = "yyyy-MM-dd";

const rangeForPeriod = (periodId) => {
  const today = new Date();
  const end = formatDate(today, dateFmt);
  switch (periodId) {
    case "last_week":
      return { dateRangeStart: formatDate(subDays(today, 7), dateFmt), dateRangeEnd: end };
    case "last_month":
      return { dateRangeStart: formatDate(subMonths(today, 1), dateFmt), dateRangeEnd: end };
    case "last_quarter":
      return { dateRangeStart: formatDate(subMonths(today, 3), dateFmt), dateRangeEnd: end };
    default:
      return { dateRangeStart: "", dateRangeEnd: "" };
  }
};

const emptyValues = {
  name: "",
  description: "",
  type: "",
  format: "",
  dateRangeStart: "",
  dateRangeEnd: "",
  note: "",
};

const formatDateTime = (date, pattern = "MMM d, yyyy, h:mm a") => {
  if (!date) return "N/A";
  const parsed = parseISO(date);
  return isValid(parsed) ? formatDate(parsed, pattern) : "N/A";
};

const GenerateReportDialog = ({
  open,
  onClose,
  mode = "create",
  initialType = null,
  report = null,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { types, formats, isLoading: isOptionsLoading } = useReportOptions();
  const [selectedPeriod, setSelectedPeriod] = useState("last_month");
  const [successData, setSuccessData] = useState(null);
  const isEdit = mode === "edit";

  const formik = useFormik({
    initialValues: emptyValues,
    validationSchema: reportFormSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const { note, ...createPayload } = values;
      if (isEdit) {
        mutation.mutate({ id: report.id, payload: values });
      } else {
        mutation.mutate(createPayload);
      }
    },
  });

  // Preload the form once options/report data is available - can't run this
  // synchronously in initialValues because types/formats come from an async
  // query and the report to edit is passed in after the dialog is opened.
  useEffect(() => {
    if (!open) return;

    if (isEdit && report) {
      setSelectedPeriod("custom");
      formik.setValues({
        name: report.name ?? "",
        description: report.description ?? "",
        type: report.type ?? "",
        format: report.format ?? "",
        dateRangeStart: report.dateRangeStart ?? "",
        dateRangeEnd: report.dateRangeEnd ?? "",
        note: "",
      });
      return;
    }

    if (!isEdit && initialType) {
      const meta = getReportTypeMeta(initialType);
      const range = rangeForPeriod("last_month");
      formik.setValues({
        ...emptyValues,
        name: meta.label,
        type: initialType,
        format: formats[0] ?? "",
        ...range,
      });
      setSelectedPeriod("last_month");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, report, initialType, formats.join(",")]);

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEdit ? updateReport({ id: report.id, payload }) : createReport(payload),
    onSuccess: (data) => {
      const status = data?.data?.status;
      const statusMessage = {
        AWAITING_APPROVAL: "Report submitted for approval.",
        GENERATING: "Report generation started - it'll appear as ready shortly.",
      }[status];
      const message =
        data?.message ?? statusMessage ?? "Report generated successfully.";
      dispatch(showToast(message, "success"));
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["reportStats"] });
      if (isEdit) queryClient.invalidateQueries({ queryKey: ["report", report.id] });
      // Keep the dialog open so the user can see the resulting status/activity
      // (e.g. AWAITING_APPROVAL) instead of losing that context to a toast.
      setSuccessData(data?.data ?? null);
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    setSelectedPeriod("last_month");
    setSuccessData(null);
    onClose();
  };

  const handlePeriodClick = (periodId) => {
    setSelectedPeriod(periodId);
    if (periodId !== "custom") {
      const range = rangeForPeriod(periodId);
      formik.setValues({ ...formik.values, ...range });
    }
  };

  const typeOptions = types.map((t) => ({
    label: getReportTypeMeta(t).label,
    value: t,
  }));
  const formatOptions = formats.map((f) => ({
    label: formatOptionLabel(f),
    value: f,
  }));

  if (successData) {
    const statusMeta = getReportStatusMeta(successData.status);

    return (
      <StyledDialog open={open} onClose={handleClose}>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconWrapper>
              <CheckCircleOutlineIcon
                sx={{ color: "success.main", fontSize: 22 }}
              />
            </IconWrapper>
            <Box>
              <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
                {isEdit ? "Report Updated" : "Report Created"}
              </DialogTitle>
              <Typography variant="caption" color="text.secondary">
                {successData.name}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box
            sx={{
              display: "inline-flex",
              alignSelf: "flex-start",
              px: 1.5,
              py: 0.3,
              borderRadius: "999px",
              fontSize: "0.8rem",
              fontWeight: 500,
              backgroundColor: statusMeta.bg,
              color: statusMeta.color,
            }}
          >
            {statusMeta.label}
          </Box>

          <Typography variant="body2" color="text.secondary">
            Created by {successData.createdByName ?? "N/A"}
          </Typography>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Activity
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {successData.activity?.length ? (
                successData.activity.map((entry) => (
                  <Box
                    key={entry.id}
                    sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 1.5 }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {formatOptionLabel(entry.action)}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        by {entry.actorName ?? "N/A"}
                      </Typography>
                    </Typography>
                    {entry.note && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {entry.note}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(entry.occurredAt)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No activity recorded yet.
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", gap: 1, px: 3, pb: 2 }}>
          <CustomButton onClick={handleClose} sx={{ minWidth: 120 }}>
            Close
          </CustomButton>
        </DialogActions>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconWrapper>
            <AssignmentOutlinedIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </IconWrapper>
          <Box>
            <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
              {isEdit ? "Edit Report" : "Generate Report"}
            </DialogTitle>
            <Typography variant="caption" color="text.secondary">
              {isEdit
                ? "Only allowed while the report is awaiting approval"
                : "Fill in the details for this report"}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <FormTextInput formik={formik} name="name" label="Report Name" />
        <FormTextInput
          formik={formik}
          name="description"
          label="Description (optional)"
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <FormSelectInput
            formik={formik}
            name="type"
            label="Report Type"
            options={typeOptions}
            isDisabled={isOptionsLoading || (!isEdit && Boolean(initialType))}
          />
          <FormSelectInput
            formik={formik}
            name="format"
            label="Format"
            options={formatOptions}
            isDisabled={isOptionsLoading}
          />
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Select Period
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {periods.map((p) => (
              <PeriodButton
                key={p.id}
                isActive={selectedPeriod === p.id}
                onClick={() => handlePeriodClick(p.id)}
                type="button"
              >
                {p.label}
              </PeriodButton>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <FormTextInput
            formik={formik}
            name="dateRangeStart"
            label="From"
            type="date"
            isDisabled={selectedPeriod !== "custom"}
          />
          <FormTextInput
            formik={formik}
            name="dateRangeEnd"
            label="To"
            type="date"
            isDisabled={selectedPeriod !== "custom"}
          />
        </Box>

        {isEdit && (
          <FormTextInput
            formik={formik}
            name="note"
            label="Note (optional)"
            placeholder="Reason for this update"
          />
        )}

        {mutation.isError && (
          <CustomErrorMessage
            message={mutation.error?.message ?? "Something went wrong. Please try again."}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", gap: 1, px: 3, pb: 2 }}>
        <CustomButton
          variantType="outline"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={formik.handleSubmit}
          disabled={mutation.isPending}
          sx={{ minWidth: 120 }}
        >
          {mutation.isPending ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Generate"
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default GenerateReportDialog;
